import React, { useEffect, useState } from "react";
import ConnectionRequiredPage from "@/components/dashboard/ConnectionRequiredPage";
import api from "@/lib/api";
import AdminPartnerProductsQueue from "@/pages/admin/AdminPartnerProductsQueue";

// Phase 13.9 — Delivery & operations stub pages.
// Each page renders real data where available and shows a clean
// "Connection Required" banner for the specific external service it depends on.

export function DriverLiveMap() {
  const [drivers, setDrivers] = useState([]);
  useEffect(() => {
    api.get("/admin/delivery/drivers").then(({ data }) => setDrivers(data || []))
      .catch(() => setDrivers([]));
  }, []);
  return (
    <ConnectionRequiredPage
      title="Driver Live Map"
      description="Live driver positions on a map. Requires a map provider."
      missingEnv={["MAPBOX_TOKEN", "HERE_API_KEY", "TOMTOM_API_KEY"]}
    >
      <div className="glass-panel p-5" data-testid="driver-roster-fallback">
        <div className="font-bebas tracking-[0.18em] uppercase text-white text-sm mb-2">
          Driver roster ({drivers.length})
        </div>
        <ul className="text-xs space-y-1">
          {drivers.slice(0, 25).map((d) => (
            <li key={d.id} className="text-lifted-dim">
              {d.name || d.email} —{" "}
              <span className="text-white">{d.status || "—"}</span>
              {d.availability && ` · ${d.availability}`}
            </li>
          ))}
        </ul>
      </div>
    </ConnectionRequiredPage>
  );
}

export function DriverDocuments() {
  return (
    <ConnectionRequiredPage
      title="Driver Documents"
      description="Driver license uploads, registrations, and proof-of-insurance."
      missingEnv={["S3_BUCKET", "AWS_ACCESS_KEY_ID", "AWS_SECRET_ACCESS_KEY"]}
    />
  );
}

export function ManualRouting() {
  return (
    <ConnectionRequiredPage
      title="Manual Routing"
      description="Drag-and-drop driver routing & ETAs."
      missingEnv={["MAPBOX_TOKEN", "HERE_API_KEY"]}
    />
  );
}

export function IDVerifications() {
  return (
    <ConnectionRequiredPage
      title="ID Verifications"
      description="Background checks and ID verification for drivers/partners."
      missingEnv={["CHECKR_API_KEY", "ONFIDO_API_KEY"]}
    />
  );
}

export function InsuranceCompliance() {
  return (
    <ConnectionRequiredPage
      title="Insurance Compliance"
      description="Driver/partner insurance docs, renewals, expirations."
      missingEnv={["S3_BUCKET", "AWS_ACCESS_KEY_ID"]}
    />
  );
}

export function ForceOrder() {
  return (
    <ConnectionRequiredPage
      title="Force Order"
      description="Force-create a delivery for testing. Requires payment provider for paid orders; works in dry-run otherwise."
      missingEnv={["STRIPE_SECRET_KEY"]}
    />
  );
}

export function AdminInventory() {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    api.get("/admin/shopify/migration/status").then(({ data }) => setCounts(data));
  }, []);
  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-6"
      data-testid="admin-inventory-page"
    >
      <header>
        <div className="eyebrow">Catalog</div>
        <h1 className="font-anton text-3xl uppercase mt-2">Inventory</h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">
          Active products + classification snapshot. Manage stock + same-day
          flags in <a href="/admin/shopify-migration" className="text-lifted-green">Products</a>.
        </p>
      </header>
      {counts && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Total" value={counts.products_total} />
          <Stat label="Gear" value={counts.gear_total} />
          <Stat label="Accessories" value={counts.accessories_total} />
          <Stat label="Unclassified" value={counts.unclassified_total}
                tone={counts.unclassified_total > 0 ? "warn" : "ok"} />
        </div>
      )}
    </div>
  );
}

export function ProductApprovals() {
  // Iter77 — Replaced the placeholder stub with the unified Iter76 admin
  // queue so /admin/product-approvals, /admin/partners/product-approvals,
  // and /admin/franchises/product-approvals all show the real pending
  // partner/franchisee submissions from the unified pipeline.
  return <AdminPartnerProductsQueue />;
}

export function AdminLocations() {
  const [zones, setZones] = useState([]);
  useEffect(() => {
    api.get("/admin/supplies/delivery-zones").then(({ data }) =>
      setZones(data || [])).catch(() => setZones([]));
  }, []);
  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-6"
      data-testid="admin-locations-page"
    >
      <header>
        <div className="eyebrow">Operations</div>
        <h1 className="font-anton text-3xl uppercase mt-2">Locations</h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">
          Delivery zones. Live geocoding requires a map provider — text data
          works without one.
        </p>
      </header>
      <div className="glass-panel p-5" data-testid="locations-list">
        <div className="text-xs text-lifted-dim mb-2">
          {zones.length} zone(s)
        </div>
        <ul className="space-y-1 text-sm">
          {zones.map((z) => (
            <li key={z.id}>
              <span className="font-bebas tracking-wider uppercase">{z.name}</span>
              <span className="text-lifted-dim text-xs ml-2">
                · {z.zip_codes?.length || 0} zips · base ${(z.base_fee_cents / 100).toFixed(2)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function AICompliance() {
  return (
    <ConnectionRequiredPage
      title="AI Compliance"
      description="Automated content and compliance scanning of partner submissions, affiliate posts, and product descriptions."
      missingEnv={["EMERGENT_LLM_KEY"]}
    />
  );
}

export function ShopifyAPISetup() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/admin/shopify/migration/status").then(({ data }) => setStatus(data));
  }, []);
  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-6"
      data-testid="shopify-setup-page"
    >
      <header>
        <div className="eyebrow">Integration</div>
        <h1 className="font-anton text-3xl uppercase mt-2">Shopify API Setup</h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">
          Current Shopify integration runs on the public storefront crawl. Add
          an Admin API token to unlock richer product, customer, and order data.
        </p>
      </header>
      {status && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Products" value={status.products_total} />
          <Stat label="Collections" value={status.collections_total} />
          <Stat label="Mode" value={status.provider_mode} mono />
          <Stat label="Scheduler" value={status.scheduler_enabled ? "Enabled" : "Disabled"} />
        </div>
      )}
      <div className="glass-panel p-5 border border-amber-400/30">
        <div className="font-bebas tracking-[0.18em] uppercase text-amber-200 text-sm mb-2">
          Optional credentials
        </div>
        <p className="text-xs text-lifted-dim mb-3">
          Public crawl already imported {status?.products_total ?? "—"} products.
          Add these only if you need order/customer history or deeper inventory sync.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {["SHOPIFY_ADMIN_API_TOKEN", "SHOPIFY_STOREFRONT_TOKEN", "SHOPIFY_API_SECRET"].map((e) => (
            <code key={e}
              className="text-xs bg-amber-500/10 text-amber-100 border border-amber-400/20 px-2 py-0.5 rounded">
              {e}
            </code>
          ))}
        </div>
      </div>
    </div>
  );
}

export function HereMapsSetup() {
  return (
    <ConnectionRequiredPage
      title="HERE Maps"
      description="HERE provides routing, geocoding, and ETAs for the driver dashboard."
      missingEnv={["HERE_API_KEY"]}
      obtainAt="https://platform.here.com/portal/"
    />
  );
}

export function AdminContracts() {
  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-6"
      data-testid="admin-contracts-page"
    >
      <header>
        <div className="eyebrow">Legal</div>
        <h1 className="font-anton text-3xl uppercase mt-2">Contracts</h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">
          Affiliate agreement, driver agreement, partner agreement, and
          franchisee agreement. Members sign once during onboarding; admins can
          view signed copies.
        </p>
      </header>
      <div className="glass-panel p-5 text-sm text-lifted-dim" data-testid="contracts-list">
        <ul className="space-y-2">
          <li>· Affiliate Agreement — auto-presented on Lift-Off Tier purchase or access-code redemption.</li>
          <li>· Driver Agreement — presented during driver application.</li>
          <li>· Partner Store Agreement — presented during partner application.</li>
          <li>· Franchisee Agreement — presented during franchise application.</li>
        </ul>
        <p className="text-xs mt-4">
          E-signature storage requires S3/Cloudinary. Until connected, signed
          attestations are stored as DB rows with timestamp + IP.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, tone, mono }) {
  const toneCls =
    tone === "warn"
      ? "border-amber-400/30 text-amber-200"
      : tone === "ok"
      ? "border-lifted-green/30 text-lifted-green"
      : "border-white/10 text-white";
  return (
    <div className={`glass-panel p-3 border ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-wider text-lifted-dim">{label}</div>
      <div className={`mt-1 font-anton text-xl ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
